import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaEdit, FaTrashAlt, FaPlus, FaCheck, FaTimes } from 'react-icons/fa';
import { useMyContext } from '../MyContext'; // ✅ Added context

const apiGatewayUrl = 'https://m082qhj8m5.execute-api.us-east-1.amazonaws.com/prod';

const ClientSetupTab = () => {
  const { refreshClientCodes } = useMyContext(); // ✅ Pull from context

  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [newClient, setNewClient] = useState({
    clientcode: '',
    name: '',
    shortcallsduration: '',
    passingscore: ''
  });

  const [editIndex, setEditIndex] = useState(null);

  const fetchAllClients = async () => {
    try {
      const response = await axios.get(`${apiGatewayUrl}/clientmaster`);
      if (Array.isArray(response.data)) {
        setClients(response.data);
        setFilteredClients(response.data);
      } else {
        console.error('Expected array but got:', response.data);
        setClients([]);
        setFilteredClients([]);
      }
    } catch (err) {
      console.error('Failed to fetch clients:', err);
      setClients([]);
      setFilteredClients([]);
    }
  };

  useEffect(() => {
    fetchAllClients();
  }, []);

  useEffect(() => {
    const filtered = clients.filter(client =>
      client.clientcode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredClients(filtered);
  }, [searchTerm, clients]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewClient(prev => ({ ...prev, [name]: value }));
  };

  const handleAddClient = async () => {
    try {
      await axios.post(`${apiGatewayUrl}/clientmaster`, newClient);
      setNewClient({ clientcode: '', name: '', shortcallsduration: '', passingscore: '' });
      fetchAllClients();
      refreshClientCodes(); // ✅ Update global dropdown
    } catch (err) {
      console.error('Failed to add client:', err);
    }
  };

  const handleDeleteClient = async (clientcode) => {
    const confirm = window.confirm(`Delete client "${clientcode}"?`);
    if (!confirm) return;
    try {
      await axios.delete(`${apiGatewayUrl}/clientmaster`, { data: { clientcode } });
      fetchAllClients();
      refreshClientCodes(); // ✅ Optional: keep global list clean
    } catch (err) {
      console.error('Failed to delete client:', err);
    }
  };

  const handleEditClient = (index) => {
    setEditIndex(index);
    setNewClient(filteredClients[index]);
  };

  const handleUpdateClient = async () => {
    try {
      await axios.put(`${apiGatewayUrl}/clientmaster`, newClient);
      setNewClient({ clientcode: '', name: '', shortcallsduration: '', passingscore: '' });
      setEditIndex(null);
      fetchAllClients();
      refreshClientCodes(); // ✅ Keep client dropdown updated
    } catch (err) {
      console.error('Failed to update client:', err);
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Client Setup</h2>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by Client Code or Name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border p-2 w-full mb-4 rounded"
        />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <input
            type="text"
            name="clientcode"
            placeholder="Client Code"
            value={newClient.clientcode}
            onChange={handleInputChange}
            className="border p-2 rounded"
          />
          <input
            type="text"
            name="name"
            placeholder="Name"
            value={newClient.name}
            onChange={handleInputChange}
            className="border p-2 rounded"
          />
          <input
            type="number"
            name="shortcallsduration"
            placeholder="Short Calls Threshold"
            value={newClient.shortcallsduration}
            onChange={handleInputChange}
            className="border p-2 rounded"
          />
          <input
            type="number"
            name="passingscore"
            placeholder="Passing Score"
            value={newClient.passingscore}
            onChange={handleInputChange}
            className="border p-2 rounded"
          />
        </div>

        <div className="mb-4">
          {editIndex !== null ? (
            <>
              <button
                onClick={handleUpdateClient}
                className="bg-blue-600 text-white px-4 py-2 rounded mr-2"
              >
                <FaCheck className="inline mr-1" /> Update
              </button>
              <button
                onClick={() => {
                  setEditIndex(null);
                  setNewClient({ clientcode: '', name: '', shortcallsduration: '', passingscore: '' });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded"
              >
                <FaTimes className="inline mr-1" /> Cancel
              </button>
            </>
          ) : (
            <button
              onClick={handleAddClient}
              className="bg-green-600 text-white px-4 py-2 rounded mr-2"
            >
              <FaPlus className="inline mr-1" /> Add
            </button>
          )}
        </div>
      </div>

      <table className="min-w-full table-auto border border-gray-300">
        <thead className="bg-gray-100">
          <tr>
            <th className="border px-4 py-2">Client Code</th>
            <th className="border px-4 py-2">Name</th>
            <th className="border px-4 py-2">Short Calls Threshold</th>
            <th className="border px-4 py-2">Passing Score</th>
            <th className="border px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredClients.map((client, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="border px-4 py-2">{client.clientcode}</td>
              <td className="border px-4 py-2">{client.name}</td>
              <td className="border px-4 py-2">{client.shortcallsduration}</td>
              <td className="border px-4 py-2">{client.passingscore}</td>
              <td className="border px-4 py-2 space-x-2">
                <button
                  onClick={() => handleEditClient(index)}
                  className="bg-yellow-500 text-white px-3 py-1 rounded"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleDeleteClient(client.clientcode)}
                  className="bg-red-600 text-white px-3 py-1 rounded"
                >
                  <FaTrashAlt />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ClientSetupTab;

